import { useQuery } from 'react-query';
import { useRecoilValue } from 'recoil';
import createCachedSelector from 're-reselect';
import * as api from '../../services/api';
import useCRUD from '../useCRUD';
import * as sharedAtoms from '../../atoms/shared';
import useAccount from './useAccount';

const selector = createCachedSelector(
  (data) => data.data,
  (data, branchId) => branchId,
  (data, branchId) => {
    const mappedUniforms = (data || []).reduce((prev, uniform) => {
      prev[uniform.id] = uniform;
      return prev;
    }, {});

    const uniforms = (data || []).filter(({ branchId: uniformBranchId }) => uniformBranchId === branchId);
    const uniformOptions = uniforms.map(({ id, name, image, description }) => ({
      value: id,
      label: name,
      image,
      description
    }));

    return { mappedUniforms, uniforms, uniformOptions };
  }
)({
  keySelector: (data, branchId, storeKey) => `${storeKey.join('#')}`
});

const useUniforms = ({ branchId: customBranchId } = {}) => {
  const { accountId } = useAccount();
  const defaultBranchId = useRecoilValue(sharedAtoms.selectedBranch);
  const branchId = customBranchId ? parseInt(customBranchId, 10) : defaultBranchId;

  const storeKey = ['uniforms', accountId];

  const { isLoading, data, refetch } = useQuery(storeKey, () => api.fetchUniforms({ accountId }), {
    select: (uniformsData) => selector(uniformsData, branchId, storeKey)
  });

  const { mappedUniforms = {}, uniforms = [], uniformOptions = [] } = data || {};

  const { addItem, editItem, deleteItem } = useCRUD(
    storeKey,
    {
      addApi: (uniform) => api.addUniform({ accountId, branchId, uniform }),
      editApi: ({ id, ...uniform }) => api.updateUniform({ accountId, branchId, uniformId: id, uniform }),
      deleteApi: ({ id }) => api.deleteUniform({ accountId, branchId, uniformId: id })
    },
    { formatItem: (item) => ({ ...item, branches: [branchId] }) }
  );

  return {
    isLoading,
    refetch,
    uniforms,
    uniformOptions,
    mappedUniforms,
    deleteUniform: deleteItem,
    addUniform: addItem,
    editUniform: editItem
  };
};

export default useUniforms;
